/**
 * Manages the state for the subscriptions module.
 */

import { createReduxStore, register } from '@wordpress/data';
import SubscriptionProductType from '../valueObject/SubscriptionProductType';
import { SUBSCRIPTIONS_STORE_NAME } from '../constants';

const DEFAULT_STATE = {
	// Product IDs
	products: {
		subscription: null,
		regular: null
	},
	// The selected dates for each type
	dateYmd: {
		subscription: null,
		regular: null
	},
	// The selected timeslots for each type
	timeslot: {
		subscription: "",
		regular: ""
	},
	// Popup state - can be false, 'subscription', or 'regular'
	popupOpen: false,
	// Timeslot options for each type
	timeslotOptions: {
		subscription: [],
		regular: []
	}, 
	// Loading state for each type
	loading: {
		subscription: false,
		regular: false
	},
	// Shipping methods for each type
	shippingMethod: {
		subscription: '',
		regular: ''
	},
	// Subscription data fetched from the API
	subscriptionData: null,
	// Reserved slots for each type
	reservedSlot: {
		subscription: null,
		regular: null
	},
	// Bookable dates for each type
	bookableDates: {
		subscription: [],
		regular: []
	},
	// Formatted subscription interval
	formattedSubscriptionInterval: '',
	// Conflict message
	conflictMessage: '',
	// is booking allowed
	areSlotsAllowed: false,
};

const actions = {
	setDateYmd(type, dateYmd) {
		if ( ! SubscriptionProductType.getAll().includes(type) ) {
			throw new Error('Invalid product type');
		}

		return {
			type: 'SET_DATE_YMD',
			productType: type,
			dateYmd,
		};
	},
	setProducts(type, products) {
		if ( ! SubscriptionProductType.getAll().includes(type) ) {
			throw new Error('Invalid product type');
		}

		return {
			type: 'SET_PRODUCTS',
			sessionType: type,
			products,
		};
	},
	setTimeslot(type, timeslot) {
		if ( ! SubscriptionProductType.getAll().includes(type) ) {
			throw new Error('Invalid product type');
		}

		return {
			type: 'SET_TIMESLOT',
			productType: type,
			timeslot,
		};
	},
	setTimeslotOptions(options) {
		return {
			type: 'SET_TIMESLOT_OPTIONS',
			options,
		};
	},
	setLoading(type, isLoading) {
		if ( ! SubscriptionProductType.getAll().includes(type) ) {
			throw new Error('Invalid product type');
		}

		return {
			type: 'SET_LOADING',
			productType: type,
			isLoading,
		};
	},
	setShippingMethod(type, method) {
		if ( ! SubscriptionProductType.getAll().includes(type) ) {
			throw new Error('Invalid product type');
		}

		return {
			type: 'SET_SHIPPING_METHOD',
			productType: type,
			method,
		};
	},
	setSubscriptionData(data) {
		return {
			type: 'SET_SUBSCRIPTION_DATA',
			data,
		};
	},
	setReservedSlot(type, slot) {
		if ( ! SubscriptionProductType.getAll().includes(type) ) {
			throw new Error('Invalid product type');
		}

		return {
			type: 'SET_RESERVED_SLOT',
			productType: type,
			slot,
		};
	},
	setPopupOpen(isOpen) {
		return {
			type: 'SET_POPUP_OPEN',
			isOpen,
		};
	},
	setBookableDates(type, dates) {
		if ( ! SubscriptionProductType.getAll().includes(type) ) {
			throw new Error('Invalid product type');
		}

		return {
			type: 'SET_BOOKABLE_DATES',
			productType: type,
			dates,
		};
	},
	setFormattedSubscriptionInterval(interval) {
		return {
			type: 'SET_FORMATTED_SUBSCRIPTION_INTERVAL',
			interval,
		};
	},
	setConflictMessage(message) {
		return {
			type: 'SET_CONFLICT_MESSAGE',
			message,
		};
	},
	setAreSlotsAllowed(areSlotsAllowed) {
		return {
			type: 'SET_ARE_SLOTS_ALLOWED',
			areSlotsAllowed,
		};
	},
};

const selectors = {
	getDateYmd(state, type = 'subscription') {
		return state.dateYmd[type];
	},
	getTimeslot(state, type = 'subscription') {
		return state.timeslot[type];
	},
	getTimeslotOptions(state, type = 'subscription') {
		return state.timeslotOptions[type];
	},
	isLoading(state, type = 'subscription') {
		return state.loading[type];
	},
	getShippingMethod(state, type = 'subscription') {
		return state.shippingMethod[type];
	},
	getProducts(state, type = 'subscription') {
		return state.products[type];
	},
	getSubscriptionData(state) {
		return state.subscriptionData;
	},
	getReservedSlot(state, type = 'subscription') {
		return state.reservedSlot[type];
	},
	getBookableDates(state) {
		return state.bookableDates;
	},
	getPopupOpen(state) {
		return state.popupOpen;
	},
	getFormattedSubscriptionInterval(state) {
		return state.formattedSubscriptionInterval;
	},
	getConflictMessage(state) {
		return state.conflictMessage;
	},
	getAreSlotsAllowed(state) {
		return state.areSlotsAllowed;
	}
};

const store = createReduxStore(SUBSCRIPTIONS_STORE_NAME, {
	reducer(state = DEFAULT_STATE, action) {
		switch (action.type) {
			case 'SET_DATE_YMD':
				return {
					...state,
					dateYmd: {
						...state.dateYmd,
						[action.productType]: action.dateYmd,
					},
				};
			case 'SET_TIMESLOT':
				return {
					...state,
					timeslot: {
						...state.timeslot,
						[action.productType]: action.timeslot,
					},
				};
			case 'SET_TIMESLOT_OPTIONS':
				return {
					...state,
					timeslotOptions: action.options,
				};
			case 'SET_LOADING':
				return {
					...state,
					loading: {
						...state.loading,
						[action.productType]: action.isLoading,
					},
				};
			case 'SET_SHIPPING_METHOD':
				return {
					...state,
					shippingMethod: {
						...state.shippingMethod,
						[action.productType]: action.method,
					},
				};
			case 'SET_PRODUCTS':
				return {
					...state,
					products: {
						...state.products,
						[action.sessionType]: action.products
					}
				};
			case 'SET_SUBSCRIPTION_DATA':
				return {
					...state,
					subscriptionData: action.data,
				};
			case 'SET_RESERVED_SLOT':
				return {
					...state,
					reservedSlot: {
						...state.reservedSlot,
						[action.productType]: action.slot,
					},
				};
			case 'SET_POPUP_OPEN':
				return {
					...state,
					popupOpen: action.isOpen,
				};
			case 'SET_BOOKABLE_DATES':
				return {
					...state,
					bookableDates: {
						...state.bookableDates,
						[action.productType]: action.dates,
					},
				};
			case 'SET_FORMATTED_SUBSCRIPTION_INTERVAL':
				return {
					...state,
					formattedSubscriptionInterval: action.interval,
				};
			case 'SET_CONFLICT_MESSAGE':
				return {
					...state,
					conflictMessage: action.message,
				};
			case 'SET_ARE_SLOTS_ALLOWED':
				return {
					...state,
					areSlotsAllowed: action.areSlotsAllowed,
				}
			default:
				return state;
		}
	},
	actions,
	selectors,
});

register(store);
